// src/pages/PaymentSingleFile.jsx
// Single-file React component combining Pricing + Stripe + Razorpay flows.
// Uses browser geolocation ONLY to pick provider (Stripe for EU, Razorpay otherwise).
// Paste this file into your project (adjust import paths for your project structure if needed) and live_key.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase"; // adjust path if needed
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

// Replace with your env variables / API base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STRIPE_PUBLISHABLE = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const RAZORPAY_KEY_FALLBACK = process.env.REACT_APP_RAZORPAY_KEY_ID;

// Stripe loader
const stripePromise = loadStripe(STRIPE_PUBLISHABLE);

// // Plans (combined)
// const PLANS = [
//   {
//     name: "Free Plan",
//     priceINR: 0,
//     priceEUR: 0,
//     features: [
//       "Basic Resume Builder (1 template)",
//       "Simple Cover Letter Generator (3/month)",
//       "5 AI Interview Questions/month",
//       "Standard Resume Review",
//       "Email Support (48h response)",
//       "Job Search Tips & Resources",
//     ],
//     recommended: false,
//     cta: "Get Started",
//   },
//   {
//     name: "Premium Plan",
//     priceINR: 499,
//     priceEUR: 5.0,
//     features: [
//       "Advanced Resume Builder (10+ templates)",
//       "AI-Powered Cover Letters (Unlimited)",
//       "Unlimited AI Interview Questions",
//       "Live 1:1 Interview Coaching",
//       "Priority 24/7 Support",
//       "ATS-Optimized Resume & LinkedIn",
//       "Personalized Job Matches",
//       "Career Path Planning",
//       "Salary Negotiation Guide",
//       "Access to Exclusive Webinars",
//     ],
//     recommended: true,
//     cta: "Subscribe Now",
//   },
// ];

/* -------------------------
   Utilities: browser geolocation & reverse-geocode
   ------------------------- */
async function reverseGeocodeCountry(lat, lon, timeoutMs = 5000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`;
    const resp = await fetch(url, { signal: controller.signal });
    clearTimeout(id);
    if (!resp.ok) return null;
    const json = await resp.json();
    return json?.countryCode ?? json?.country_code ?? null;
  } catch (e) {
    clearTimeout(id);
    return null;
  }
}

async function detectRegionBrowser() {
  if (!("geolocation" in navigator)) return "UNKNOWN";

  const getPos = () =>
    new Promise((resolve, reject) => {
      let resolved = false;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!resolved) {
            resolved = true;
            resolve(pos);
          }
        },
        (err) => {
          if (!resolved) {
            resolved = true;
            reject(err);
          }
        },
        { maximumAge: 60_000, timeout: 8000, enableHighAccuracy: false }
      );
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error("geolocation timeout"));
        }
      }, 9000);
    });

  try {
    const pos = await getPos();
    const lat = pos.coords.latitude;
    const lon = pos.coords.longitude;
    const cc = await reverseGeocodeCountry(lat, lon, 5000);
    if (!cc) return "UNKNOWN";
    const c = String(cc).toUpperCase();
    if (c === "IN") return "IN";

    const euList = new Set([
      "AL","AD","AM","AT","AZ","BY","BE","BA","BG","HR","CY","CZ","DK","EE","FI","FR",
      "GE","DE","GR","HU","IS","IE","IT","KZ","XK","LV","LI","LT","LU","MT","MD","MC",
      "ME","NL","MK","NO","PL","PT","RO","RU","SM","RS","SK","SI","ES","SE","CH","TR",
      "UA","GB"
    ]);
    if (euList.has(c)) return "EU";
    return "OTHER";
  } catch (e) {
    return "UNKNOWN";
  }
}

/* -------------------------
   Billing address small form (used by Stripe flow)
   ------------------------- */
function BillingAddressForm({ billing, setBilling }) {
  const update = (k, v) => setBilling((p) => ({ ...(p || {}), [k]: v }));
  return (
    <div className="space-y-2 p-4 bg-white/5 rounded-md text-sm text-white mb-4">
      <div className="font-semibold mb-2">Billing / Export details (required for EUR)</div>

      <div className="grid grid-cols-1 gap-2">
        <input
          value={billing?.name || ""}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Full name"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
        <input
          value={billing?.email || ""}
          onChange={(e) => update("email", e.target.value)}
          placeholder="Email (optional)"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
        <input
          value={billing?.line1 || ""}
          onChange={(e) => update("line1", e.target.value)}
          placeholder="Address line 1"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
        <input
          value={billing?.line2 || ""}
          onChange={(e) => update("line2", e.target.value)}
          placeholder="Address line 2 (optional)"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            value={billing?.city || ""}
            onChange={(e) => update("city", e.target.value)}
            placeholder="City"
            className="p-2 rounded bg-white/5 text-black"
          />
          <input
            value={billing?.state || ""}
            onChange={(e) => update("state", e.target.value)}
            placeholder="State / Region"
            className="p-2 rounded bg-white/5 text-black"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={billing?.postal_code || ""}
            onChange={(e) => update("postal_code", e.target.value)}
            placeholder="Postal code"
            className="p-2 rounded bg-white/5 text-black"
          />
          <input
            value={billing?.country || ""}
            onChange={(e) => update("country", e.target.value.toUpperCase())}
            placeholder="Country (2-letter ISO, e.g. DE, FR)"
            className="p-2 rounded bg-white/5 text-black"
            maxLength={2}
          />
        </div>
        <input
          value={billing?.phone || ""}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="Phone (optional)"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
      </div>
    </div>
  );
}

/* -------------------------
   Stripe inner checkout component
   ------------------------- */
function StripeCheckoutForm({ clientSecret, amountEUR, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    if (!stripe || !elements) {
      setError("Stripe initialization failed. Please refresh.");
      return;
    }

    setLoading(true);

    try {
      // 1. Confirm the payment with Stripe
      // use "if_required" to handle 3D Secure redirects only when necessary
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { 
          return_url: window.location.href 
        },
        redirect: "if_required",
      });

      // 2. Handle Stripe immediate errors (e.g., card declined)
      if (result.error) {
        setError(result.error.message || "Payment failed. Please check your card details.");
        setLoading(false);
        return;
      }

      // 3. If we get here, payment was processed or succeeded
      const pi = result.paymentIntent;
      if (!pi) {
        setError("No payment confirmation received from Stripe.");
        setLoading(false);
        return;
      }

      // 4. Verify with backend to trigger the 30-day Premium upgrade
      // This matches your requirement to sync logic with Razorpay
      const freshUser = getAuth().currentUser;
      if (!freshUser) {
        setError("User session lost. Please sign in again.");
        setLoading(false);
        return;
      }

      try {
        const verifyRes = await axios.post(
          `${API_BASE_URL}/api/stripe/verify-payment`,
          { 
            payment_intent_id: pi.id,
            uid: freshUser.uid,
            plan: "premium" // This tells the backend to apply the 30-day logic
          },
          { 
            withCredentials: true, 
            headers: { "Content-Type": "application/json" } 
          }
        );

        if (verifyRes.data.success) {
          // Success! This will trigger the modal close and page reload in the parent
          onSuccess();
        } else {
          setError(verifyRes.data.error?.message || "Payment succeeded but verification failed. Please contact support.");
        }
      } catch (verifyErr) {
        console.error("Backend verification error:", verifyErr);
        setError("Payment verified at Stripe, but failed to update your account. Please do not pay again and contact support.");
      }

    } catch (err) {
      console.error("confirmPayment error", err);
      setError(err?.message || "An unexpected error occurred during checkout.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-md border p-4 bg-white text-black shadow-sm">
        <div className="mb-4 text-sm border-b pb-2">
          <p className="font-medium">Subscription: Premium Plan</p>
          <p className="text-gray-600">Total: <strong>€{amountEUR.toFixed(2)}</strong> (One-time, 30 days access)</p>
        </div>
        
        <div className="mb-4">
          {/* Stripe UI Component */}
          <PaymentElement />
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200 mb-4">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button 
            type="submit" 
            disabled={!stripe || loading} 
            className="flex-1 px-4 py-2.5 rounded-xl bg-[#3C0F6C] text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#2C0B4F] transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              `Pay €${amountEUR.toFixed(2)}`
            )}
          </button>
          
          <button 
            type="button" 
            onClick={onCancel} 
            className="px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all" 
            disabled={loading}
          >
            Cancel
          </button>
        </div>

        <div className="mt-4 pt-3 border-t">
          <p className="text-[10px] text-gray-500 text-center">
            Payments are secured by Stripe. By subscribing, you get 30 days of Premium access.
          </p>
          {process.env.NODE_ENV !== 'production' && (
            <p className="mt-2 text-[10px] text-indigo-600 font-mono text-center bg-indigo-50 p-1 rounded">
              TEST MODE: Use 4242 4242 4242 4242
            </p>
          )}
        </div>
      </div>
    </form>
  );
}

/* -------------------------
   Main single-file page component
   ------------------------- */
export default function PaymentSingleFile({ userData, setActiveTab }) {
  const navigate = useNavigate();
  const [backendPlans, setBackendPlans] = useState([]);
  const [isFetchingPlans, setIsFetchingPlans] = useState(true);


  // UI / state
  const [loadingStates, setLoadingStates] = useState({});
  const [error, setError] = useState(null);
  const [razorpayReady, setRazorpayReady] = useState(false);

  // Stripe modal state + billing
  const [clientSecret, setClientSecret] = useState("");
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [activeAmountEUR, setActiveAmountEUR] = useState(5.0);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [billing, setBilling] = useState(() => {
    const u = getAuth().currentUser;
    return {
      name: u?.displayName || userData?.name || "",
      email: u?.email || userData?.email || "",
      line1: userData?.address_line1 || "",
      line2: userData?.address_line2 || "",
      city: userData?.city || "",
      state: userData?.state || "",
      postal_code: userData?.postal_code || "",
      country: (userData?.country || "").toUpperCase() || "",
      phone: userData?.phone || "",
    };
  });

  // Premium detection (client side)
  const isPremiumUser = (userData?.role || "").toLowerCase() === "premium";
  
  // Debug logging
  useEffect(() => {
    console.log("💰 Pricing component - userData:", userData);
    console.log("💰 Pricing component - role:", userData?.role);
    console.log("💰 Pricing component - isPremiumUser:", isPremiumUser);
  }, [userData, isPremiumUser]);

  useEffect(() => {
  const fetchPlans = async () => {
    try {
      // Change the URL here
      const res = await axios.get(`${API_BASE_URL}/api/billing/config`);
      if (res.data.success && Array.isArray(res.data.plans)) {
        setBackendPlans(res.data.plans);
      } else {
        setError("Invalid plan config from server.");
      }
    } catch (err) {
      console.error("Failed to fetch plans:", err);
      setError("Could not load pricing information.");
    } finally {
      setIsFetchingPlans(false);
    }
  };
  fetchPlans();
}, []);

//   useEffect(() => {
//   const fetchPlans = async () => {
//     try {
//       const res = await axios.get(`${API_BASE_URL}/api/payments/config`);
//       if (res.data.success && Array.isArray(res.data.plans)) {
//         setBackendPlans(res.data.plans);
//       } else {
//         setError("Invalid plan config from server.");
//       }
//     } catch (err) {
//       console.error("Failed to fetch plans:", err);
//       setError("Could not load pricing information.");
//     } finally {
//       setIsFetchingPlans(false);
//     }
//   };
//   fetchPlans();
// }, []);


  // Razorpay script load
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => setRazorpayReady(true);
    script.onerror = () => setError("Failed to load payment system.");
    document.body.appendChild(script);
    return () => {
      // do not remove script on unmount; keep it for subsequent usage
    };
  }, []);

  // Auth listen (to reflect login changes)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged((u) => setIsAuthenticated(!!u));
    return () => unsub();
  }, []);

  // Browser-only region detection (EU => Stripe; otherwise Razorpay)
  // const [region, setRegion] = useState("UNKNOWN");
  // const [detecting, setDetecting] = useState(true);
  // useEffect(() => {
  //   let mounted = true;
  //   (async () => {
  //     try {
  //       setDetecting(true);
  //       const r = await detectRegionBrowser();
  //       if (!mounted) return;
  //       setRegion(r);
  //     } finally {
  //       if (mounted) setDetecting(false);
  //     }
  //   })();
  //   return () => {
  //     mounted = false;
  //   };
  // }, []);

  // const provider = region === "EU" ? "stripe" : "razorpay";

  // Use environment variable set at build time
  const provider = process.env.REACT_APP_PAYMENT_PROVIDER;
  const detecting = false;

  /* -------------------------
     Razorpay Payment Handler (INR)
     ------------------------- */
  const handleRazorpayPayment = async (plan) => {
    try {
      if (!razorpayReady) {
        setError("Payment system is not ready yet.");
        return;
      }
      if (isPremiumUser && plan.name === "Premium Plan") return;

      setLoadingStates((p) => ({ ...p, [plan.name]: true }));
      setError(null);

      const { currentUser } = getAuth();
      if (!currentUser) {
        setError("You need to sign in to subscribe.");
        navigate("/auth");
        setLoadingStates((p) => ({ ...p, [plan.name]: false }));
        return;
      }

      const res = await axios.post(`${API_BASE_URL}/api/payments/create-order`, {
        amount: plan.priceINR * 100,
        currency: "INR",
        receipt: `order_rcptid_${Date.now()}`,
        notes: { plan_name: plan.name, user_id: currentUser.uid },
      });

      const order = (res?.data?.data?.order) || (res?.data?.order);
      const key_id = (res?.data?.data?.key_id) || (res?.data?.key_id) || RAZORPAY_KEY_FALLBACK;
      if (!order || !key_id) throw new Error("Invalid backend response");

      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Aspirant Suite",
        description: `${plan.name} Subscription`,
        order_id: order.id,
        theme: { color: "#3C0F6C" },
        prefill: {
          name: currentUser.displayName || "",
          email: currentUser.email || "",
          contact: currentUser.phoneNumber || "",
        },
        handler: async function (response) {
          console.log("✅ Razorpay payment handler called:", {
            order_id: response.razorpay_order_id,
            payment_id: response.razorpay_payment_id,
            has_signature: !!response.razorpay_signature
          });
          try {
            console.log("📞 Calling /api/payments/verify-payment...");
            const verifyRes = await axios.post(`${API_BASE_URL}/api/payments/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              uid: currentUser.uid,
              amount: plan.priceINR,
              plan: "premium",
            });
            console.log("✅ Verify payment response:", verifyRes.data);
            // your backend status structure may differ — check verifyRes.data
            if (verifyRes?.data?.success) {
              alert("Payment successful! Your account has been upgraded to Premium.");
              // Reload page to refresh userData and show premium status
              window.location.reload();
            } else {
              console.error("❌ Verification failed - response:", verifyRes.data);
              alert("Payment completed but verification failed on server; contact support.");
            }
          } catch (err) {
            console.error("❌ Payment verification failed:", err);
            console.error("❌ Error details:", err.response?.data || err.message);
            alert(`Payment verification failed: ${err.response?.data?.error?.message || err.message}. Check console for details.`);
          } finally {
            setLoadingStates((p) => ({ ...p, [plan.name]: false }));
          }
        },
        modal: {
          ondismiss: () => {
            setError("Payment was cancelled.");
            setLoadingStates((p) => ({ ...p, [plan.name]: false }));
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        setError(response.error.description || "Payment failed. Please try again.");
        setLoadingStates((p) => ({ ...p, [plan.name]: false }));
      });
      rzp.open();
    } catch (err) {
      console.error("Payment initiation failed:", err);
      setError("Payment initiation failed. Please try again.");
      setLoadingStates((p) => ({ ...p, [plan.name]: false }));
    }
  };

  /* -------------------------
     Stripe: create PaymentIntent -> show PaymentElement modal
     ------------------------- */
  const startStripeCheckout = async (amountEUR) => {
    try {
      setError(null);
      setClientSecret("");
      setCreatingIntent(true);

      const freshUser = getAuth().currentUser;
      if (!freshUser) {
        setError("Please sign in to purchase.");
        navigate("/auth");
        return;
      }

      // Validate required billing fields for India export if sending EUR
      if (amountEUR > 0) {
        const name = (billing?.name || "").trim();
        const line1 = (billing?.line1 || "").trim();
        const country = (billing?.country || "").trim();
        if (!name || !line1 || !country || country.length !== 2) {
          setError("Please provide full billing name, address line1 and 2-letter country code (e.g. DE).");
          return;
        }
      }

      const cents = Math.round(amountEUR * 100);
      const res = await axios.post(
        `${API_BASE_URL}/api/stripe/create-payment-intent`,
        {
          amount: cents,
          currency: "EUR",
          metadata: { uid: freshUser.uid, email: freshUser.email, name: freshUser.displayName },
          description: "Premium plan",
          uid: freshUser.uid,
          billing,
        },
        { withCredentials: true, headers: { "Content-Type": "application/json" } }
      );

      const cs = res?.data?.data?.paymentIntent?.client_secret || res?.data?.paymentIntent?.client_secret;
      if (!cs) {
        console.error("missing client_secret", res?.data);
        throw new Error("Missing client_secret from server");
      }

      setClientSecret(cs);
      setActiveAmountEUR(amountEUR);
      setShowStripeModal(true);
    } catch (err) {
      console.error("create-payment-intent failed", err);
      setError(err?.response?.data?.error?.message || err?.message || "Failed to start payment.");
    } finally {
      setCreatingIntent(false);
    }
  };

  /* -------------------------
     Free plan handler: update Firestore user role to basic (minimal)
     ------------------------- */
  const handleFreePlan = async () => {
    try {
      const { currentUser } = getAuth();
      if (!currentUser) {
        navigate("/auth");
        return;
      }
      if (isPremiumUser) {
        navigate("/dashboard");
        return;
      }
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, { role: "basic", updatedAt: serverTimestamp() });
      alert("You are now on the Free plan!");
      navigate("/dashboard");
    } catch (err) {
      console.error("Error updating free plan:", err);
      setError("Failed to activate Free plan.");
    }
  };

  /* -------------------------
     UI rendering
     ------------------------- */
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A0637] via-[#2C0B4F] to-[#1A0637] text-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Top banner */}
        {isPremiumUser && (
          <div className="mb-8 rounded-xl border border-[#FFD600]/30 bg-[#FFD600]/10 p-4 text-center">
            <span className="font-semibold text-[#FFD600]">You’re already on Premium.</span>{" "}
            <button onClick={() => (window.location.href = "/dashboard")} className="ml-2 underline">
              Go to Dashboard →
            </button>
          </div>
        )}

        <div className="text-center mb-6">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">Choose Your Plan</h1>
          <p className="text-lg text-gray-300 mb-2">Secure payments</p>
          {/* <div className="text-sm text-gray-300">
            {detecting ? "Detecting location (browser)..." : `Detected region: ${region} — provider: ${provider.toUpperCase()}`}
          </div> */}
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {isFetchingPlans ? (
            <div className="text-center py-20">Loading plans...</div>
          ) : (
            backendPlans.map((plan) => {
            const isPremiumPlan = plan.name === "Premium Plan";
            const shouldDisable = loadingStates[plan.name] || (isPremiumUser && (isPremiumPlan || plan.priceINR === 0));
            const ctaLabel = isPremiumUser && isPremiumPlan ? "You're Premium ✓" : isPremiumUser && plan.priceINR === 0 ? "Included in Premium" : loadingStates[plan.name] ? "Processing..." : plan.cta;

            const onClick = () => {
              if (shouldDisable) return;
              if (plan.priceINR === 0) {
                handleFreePlan();
                return;
              }
              if (provider === "stripe") {
                // Open modal first, then user fills billing form, then clicks "Proceed to payment"
                setActiveAmountEUR(plan.priceEUR);
                setShowStripeModal(true);
              } else {
                // Razorpay INR flow
                handleRazorpayPayment(plan);
              }
            };

            const showRibbonText = isPremiumPlan && isPremiumUser ? "ALREADY PREMIUM" : plan.recommended ? "MOST POPULAR" : null;

            return (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-8 transition-all duration-300 ${plan.recommended ? "bg-gradient-to-b from-[#3C0F6C] to-[#2C0B4F] border-2 border-[#FFD600]" : "bg-white/5 border border-white/10 hover:border-white/20"
                  }`}
              >
                {showRibbonText && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className={`px-6 py-2 rounded-full text-sm font-bold shadow-lg ${isPremiumPlan && isPremiumUser ? "bg-[#16a34a] text-white" : "bg-gradient-to-r from-[#FFD600] to-[#FFA800] text-[#3C0F6C]"}`}>
                      {showRibbonText}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold mb-2">
                    {plan.priceINR === 0 ? "Free" : provider === "stripe" ? `€${plan.priceEUR.toFixed(2)}` : `₹${plan.priceINR}`}
                    {plan.priceINR > 0 && <span className="text-lg text-gray-300"> one-time</span>}
                  </div>
                  {plan.priceINR === 0 && <p className="text-sm text-gray-300 mb-4">No credit card required</p>}
                  {isPremiumPlan && isPremiumUser && <p className="text-sm text-gray-300 mb-2">Your subscription is active.</p>}
                </div>

                <ul className="space-y-4 mb-8 text-gray-200">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center">
                      <svg className="h-5 w-5 text-[#FFD600] mr-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onClick}
                  disabled={shouldDisable}
                  className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${isPremiumPlan && isPremiumUser ? "bg-white/10 border border-white/20 cursor-default" : plan.recommended ? "bg-gradient-to-r from-[#FFD600] to-[#FFA800] text-[#3C0F6F]" : "bg-white/10 border border-white/20 hover:bg-white/20"} ${shouldDisable ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {ctaLabel}
                </button>
              </div>
            );
          })
        )}
        </div>

        {error && (
          <div className="mt-8 text-center text-red-400 bg-red-400/10 p-4 rounded-lg">
            {String(error)}
          </div>
        )}
      </div>

      {/* Stripe modal */}
      {showStripeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowStripeModal(false)} />
          <div className="relative w-full max-w-lg p-6">
            <div className="bg-white rounded-lg p-6 text-black shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Pay €{activeAmountEUR.toFixed(2)}</h3>
                <button onClick={() => setShowStripeModal(false)} className="text-gray-600">✕</button>
              </div>

              <BillingAddressForm billing={billing} setBilling={setBilling} />

              {!clientSecret ? (
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => startStripeCheckout(activeAmountEUR)}
                    className="px-4 py-2 rounded-md bg-indigo-600 text-white"
                    disabled={creatingIntent}
                  >
                    {creatingIntent ? "Creating..." : "Proceed to payment"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowStripeModal(false)}
                    className="px-4 py-2 rounded-md border text-sm text-slate-700 bg-white"
                    disabled={creatingIntent}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}

              {clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <StripeCheckoutForm
                    clientSecret={clientSecret}
                    amountEUR={activeAmountEUR}
                    onSuccess={() => {
                      setShowStripeModal(false);
                      window.location.reload();
                    }}
                    onCancel={() => setShowStripeModal(false)}
                  />
                </Elements>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}